import React, { Component } from 'react';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import ModalContainer from '../../../components/shared/modal/ModalContainer';
import { ModalTitle } from '../../../components/shared/modal/styled';
import { ButtonForm } from '../../../components/shared/formStyles.styled';
import { createWebhook, updateWebhook } from '../../../services/integracao/webhooks';
import { handleSessionExpired } from '../../../utils/loginHelper';
import {
  FormGrid,
  FieldBlock,
  TextInput,
  TextSelect,
  Hint,
  ModalBodyPad,
  GhostButton,
} from './webhooks.styles';

class WebhookFormModal extends Component {
  state = {
    user_id: '',
    endpoint: '',
    token_url: '',
    token_user: '',
    token_password: '',
    saving: false,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.hydrateFromProps();
    }
  }

  hydrateFromProps = () => {
    const { webhook, eligibleUsers } = this.props;
    if (webhook) {
      this.setState({
        user_id: String(webhook.user_id || ''),
        endpoint: webhook.endpoint || '',
        token_url: webhook.token_url || '',
        token_user: webhook.token_user || '',
        token_password: '',
        saving: false,
      });
      return;
    }

    const available = (eligibleUsers || []).filter((u) => !u.has_webhook);
    const first = available[0] || (eligibleUsers || [])[0];
    this.setState({
      user_id: first ? String(first.id) : '',
      endpoint: '',
      token_url: '',
      token_user: '',
      token_password: '',
      saving: false,
    });
  };

  getToken = () => {
    const { login } = this.props;
    return login?.user?.token?.access_token || null;
  };

  handleChange = (field) => (event) => {
    this.setState({ [field]: event.target.value });
  };

  handleSubmit = async () => {
    const { webhook, onSaved, onClose } = this.props;
    const token = this.getToken();
    if (!token) {
      toastr.error('Erro', 'Token de autenticação não encontrado.');
      return;
    }

    const { user_id, endpoint, token_url, token_user, token_password } = this.state;
    if (!endpoint || !String(endpoint).trim()) {
      toastr.error('Validação', 'Informe a URL do webhook.');
      return;
    }
    if (!webhook && !user_id) {
      toastr.error('Validação', 'Selecione o usuário de API.');
      return;
    }

    this.setState({ saving: true });
    try {
      const payload = {
        endpoint: endpoint.trim(),
        token_url: token_url.trim() || null,
        token_user: token_user.trim() || null,
      };

      if (token_password !== '') {
        payload.token_password = token_password;
      } else if (!webhook) {
        payload.token_password = null;
      }

      let response;
      if (webhook) {
        response = await updateWebhook(token, webhook.id, payload);
      } else {
        response = await createWebhook(token, {
          ...payload,
          user_id: Number(user_id),
        });
      }

      if (response.status === 401) {
        handleSessionExpired(this.props.dispatch);
        return;
      }

      if (response.status >= 200 && response.status < 300) {
        toastr.success('Sucesso', webhook ? 'Webhook atualizado.' : 'Webhook salvo.');
        onSaved && onSaved();
        onClose && onClose();
        return;
      }

      const message =
        response.data?.message?.endpoint?.[0] ||
        response.data?.message?.user_id?.[0] ||
        response.data?.message ||
        'Não foi possível salvar o webhook.';
      toastr.error('Erro', typeof message === 'string' ? message : 'Falha na validação.');
    } catch (error) {
      console.error(error);
      toastr.error('Erro', 'Falha ao salvar webhook.');
    } finally {
      this.setState({ saving: false });
    }
  };

  renderHeader = () => (
    <ModalTitle>{this.props.webhook ? 'Editar WebHook' : 'Novo WebHook'}</ModalTitle>
  );

  renderFooter = () => (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <GhostButton type="button" onClick={this.props.onClose} disabled={this.state.saving}>
        Cancelar
      </GhostButton>
      <ButtonForm type="button" onClick={this.handleSubmit} disabled={this.state.saving}>
        {this.state.saving ? 'Salvando...' : 'Salvar'}
      </ButtonForm>
    </div>
  );

  renderUserOptions = () => {
    const { webhook, eligibleUsers } = this.props;
    const users = eligibleUsers || [];

    if (webhook) {
      const current = users.find((u) => Number(u.id) === Number(webhook.user_id));
      const label = current
        ? `${current.nome || current.email} (#${current.id})`
        : `Usuário #${webhook.user_id}`;
      return <option value={webhook.user_id}>{label}</option>;
    }

    return users
      .filter((u) => !u.has_webhook)
      .map((u) => (
        <option key={u.id} value={u.id}>
          {(u.nome || u.email || `Usuário #${u.id}`) + ` (#${u.id})`}
        </option>
      ));
  };

  render() {
    const { isOpen, onClose, webhook } = this.props;
    const { user_id, endpoint, token_url, token_user, token_password } = this.state;
    const noUsers = !webhook && (this.props.eligibleUsers || []).every((u) => u.has_webhook);

    return (
      <ModalContainer
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        header={this.renderHeader()}
        footer={this.renderFooter()}
      >
        <ModalBodyPad>
          {noUsers ? (
            <Hint>Todos os usuários de API elegíveis já possuem webhook cadastrado.</Hint>
          ) : (
            <FormGrid>
              <FieldBlock>
                Usuário de API
                <TextSelect
                  value={user_id}
                  onChange={this.handleChange('user_id')}
                  disabled={Boolean(webhook)}
                >
                  {!webhook && <option value="">Selecione...</option>}
                  {this.renderUserOptions()}
                </TextSelect>
              </FieldBlock>

              <FieldBlock>
                Endpoint (URL)
                <TextInput
                  type="url"
                  placeholder="https://cliente.example.com/hooks/fc"
                  value={endpoint}
                  onChange={this.handleChange('endpoint')}
                />
              </FieldBlock>

              <FieldBlock>
                Token URL (opcional)
                <TextInput
                  type="url"
                  placeholder="https://cliente.example.com/oauth/token"
                  value={token_url}
                  onChange={this.handleChange('token_url')}
                />
              </FieldBlock>

              <FieldBlock>
                Token user (opcional)
                <TextInput
                  type="text"
                  value={token_user}
                  onChange={this.handleChange('token_user')}
                />
              </FieldBlock>

              <FieldBlock>
                Token password (opcional)
                <TextInput
                  type="password"
                  value={token_password}
                  onChange={this.handleChange('token_password')}
                  placeholder={webhook?.has_token_password ? '•••••••• (deixe vazio para manter)' : ''}
                />
                <Hint>
                  {webhook?.has_token_password
                    ? 'Deixe em branco para manter a senha atual. Envie vazio só se quiser limpar via API.'
                    : 'Usado quando o receptor exige OAuth/basic no token_url.'}
                </Hint>
              </FieldBlock>
            </FormGrid>
          )}
        </ModalBodyPad>
      </ModalContainer>
    );
  }
}

export default connect()(WebhookFormModal);
